import { request } from '../http';

export type SearchTemplate = {
  id: string;
  slug: string;
  title: string;
  shortDescription?: string;
  longDescription?: string;
  category?: string;
  author?: string;
  version?: string;
  status?: string;
  visibility?: string;
  installCount?: number;
  favoriteCount?: number;
  ratingAverage?: number;
  featured?: boolean;
  recommended?: boolean;
  verified?: boolean;
  favorited?: boolean;
  tags?: string[];
  marketplaces?: string[];
  updateAvailable?: { installedVersion: string; latestVersion: string } | null;
  preview?: {
    category?: string;
    sourceNames?: string[];
    marketplaces?: string[];
    schedule?: Record<string, unknown>;
    filters?: Record<string, unknown>;
    keywords?: string;
    complexity?: string;
    notificationDefaults?: Record<string, unknown>;
  };
  latestVersion?: { version: string; changelog?: string };
};

export type TemplateListResponse = {
  items: SearchTemplate[];
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
};

export type TemplateInstallPayload = {
  templateId: string;
  name: string;
  version?: string;
  overrides?: Record<string, unknown>;
  isEnabled?: boolean;
};

export const templatesService = {
  categories: () => request<{ items: string[] }>('/templates/categories'),
  list: (params?: Record<string, string | number | boolean | undefined>) => {
    const q = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== '') q.set(k, String(v));
      });
    }
    const qs = q.toString();
    return request<TemplateListResponse>(`/templates${qs ? `?${qs}` : ''}`);
  },
  featured: () => request<TemplateListResponse>('/templates/featured'),
  recommended: () => request<TemplateListResponse>('/templates/recommended'),
  popular: () => request<TemplateListResponse>('/templates/popular'),
  favorites: () => request<TemplateListResponse>('/templates/favorites'),
  history: () => request<{ items: Record<string, unknown>[] }>('/templates/history'),
  get: (id: string) => request<SearchTemplate>(`/templates/${id}`),
  versions: (id: string) => request<{ items: Record<string, unknown>[] }>(`/templates/${id}/versions`),
  compare: (id: string, fromVersion: string, toVersion: string) =>
    request<{ changes: Record<string, unknown>[] }>(
      `/templates/${id}/compare?fromVersion=${encodeURIComponent(fromVersion)}&toVersion=${encodeURIComponent(toVersion)}`,
    ),
  install: (body: TemplateInstallPayload) =>
    request<{ installationId: string; search: { id: string }; templateVersion: string }>(
      '/templates/install',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  favorite: (templateId: string) =>
    request<{ favorited: boolean }>('/templates/favorite', {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    }),
  unfavorite: (templateId: string) =>
    request<{ favorited: boolean }>(`/templates/favorite/${templateId}`, { method: 'DELETE' }),
  duplicate: (templateId: string, title?: string) =>
    request<SearchTemplate>('/templates/duplicate', {
      method: 'POST',
      body: JSON.stringify({ templateId, title }),
    }),
  export: (id: string) => request<Record<string, unknown>>(`/templates/export/${id}`),
  import: (payload: Record<string, unknown>, visibility = 'organization') =>
    request<SearchTemplate>('/templates/import', {
      method: 'POST',
      body: JSON.stringify({ payload, visibility }),
    }),
  myList: () => request<TemplateListResponse>('/templates/my'),
  orgList: () => request<TemplateListResponse>('/org/templates'),
  orgCreate: (body: Record<string, unknown>) =>
    request<SearchTemplate>('/org/templates', { method: 'POST', body: JSON.stringify(body) }),
  orgUpdate: (id: string, body: Record<string, unknown>) =>
    request<SearchTemplate>(`/org/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  orgDelete: (id: string) => request<{ deleted: boolean }>(`/org/templates/${id}`, { method: 'DELETE' }),
  orgArchive: (id: string) =>
    request<{ id: string; status: string }>(`/org/templates/${id}/archive`, { method: 'POST', body: '{}' }),
  orgPublish: (templateId: string) =>
    request<{ status: string }>(`/org/templates/publish?templateId=${encodeURIComponent(templateId)}`, {
      method: 'POST',
      body: '{}',
    }),
  orgFromSearch: (searchId: string, title?: string) =>
    request<SearchTemplate>('/org/templates/from-search', {
      method: 'POST',
      body: JSON.stringify({ searchId, title }),
    }),
};
